import ApiOutlined from "@ant-design/icons/ApiOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import FundOutlined from "@ant-design/icons/FundOutlined";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import RobotOutlined from "@ant-design/icons/RobotOutlined";
import SafetyCertificateOutlined from "@ant-design/icons/SafetyCertificateOutlined";
import SwapOutlined from "@ant-design/icons/SwapOutlined";
import WarningOutlined from "@ant-design/icons/WarningOutlined";
import Skeleton from "antd/es/skeleton";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import Button from "common/Buttons/Button.tsx";
import { CopyToClipboardButton } from "common/CopyToClipboard/CopyToClipboard.tsx";
import Tabs from "common/Tabs/Tabs.tsx";
import Text from "common/Typography/Text.tsx";
import Title from "common/Typography/Title.tsx";
import React, { ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";
import {
  NEW_LOOK_SECTION_GAP,
  phoneMaxWidth,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import LookIcon from "common/Icons/LookIcon.tsx";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import {
  NEW_LOOK_RADIUS,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  ArrowLeftRight,
  Bot,
  ChartLine,
  Copy,
  Plug,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { MCP_COPY_BUTTON_ID, MCP_PAGE_ID } from "testIds.ts";

interface ServerUrlCardProps {
  isError: boolean;
  isLoading: boolean;
  isRefetching: boolean;
  onRetry: () => void;
  serverUrl?: string;
}

interface CapabilityCardProps {
  icon: ReactNode;
  translationKey: "bots" | "portfolio" | "trades";
}

type InstallationClient = "claude" | "codex" | "cursor" | "gemini" | "vscode";
type InstallationStep = "step_1" | "step_2" | "step_3" | "step_4";
type SnippetType = "command" | "configuration";

interface InstallationClientConfig {
  key: InstallationClient;
  snippetType: SnippetType;
}

const McpPage = () => {
  const { data, isError, isLoading, isFetching, refetch } =
    sharedLib.sharedApi.common.useGetMcpMetadataQuery(undefined);
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const getInstallationItems = () =>
    data?.resource ? getInstallationTabItems(t, data.resource) : [];

  if (isNewLook) {
    // Docs-like layout: the server URL leads, then the setup steps; what MCP
    // is and what it can do sit in a side column (stacked on narrow screens).
    return (
      <NewLookPage data-testid={MCP_PAGE_ID}>
        <NewLookHero>
          <NewLookAbout />
        </NewLookHero>
        <NewLookMain>
          <Panel hero title={t("mcp.server.title")}>
            <NewLookIntro>{t("mcp.server.description")}</NewLookIntro>
            <ServerUrlContent
              isError={isError}
              isLoading={isLoading}
              isRefetching={isFetching}
              onRetry={refetch}
              serverUrl={data?.resource}
            />
          </Panel>
          {data?.resource && (
            <Panel title={t("mcp.installation.title")} flush>
              <NewLookFlushIntro>
                {t("mcp.installation.description")}
              </NewLookFlushIntro>
              <InstallationTabs items={getInstallationItems()} />
            </Panel>
          )}
          <NewLookNotes />
        </NewLookMain>
        <NewLookAside>
          <NewLookCapabilities />
        </NewLookAside>
      </NewLookPage>
    );
  }

  return (
    <Page data-testid={MCP_PAGE_ID}>
      <Hero />
      <ServerUrlCard
        isError={isError}
        isLoading={isLoading}
        isRefetching={isFetching}
        onRetry={refetch}
        serverUrl={data?.resource}
      />
      {data?.resource && <Installation serverUrl={data.resource} />}
      <Capabilities />
      <SecurityNote />
      <AiDisclaimer />
    </Page>
  );
};

export default React.memo(McpPage);

const Hero = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <HeroSection>
      <IconBadge aria-hidden="true">
        <LookIcon
          classic={<ApiOutlined />}
          newLook={<Plug size={20} strokeWidth={NEW_LOOK_ICON_STROKE} />}
        />
      </IconBadge>
      <Eyebrow>{t("mcp.eyebrow")}</Eyebrow>
      <HeroTitle level={1}>{t("mcp.title")}</HeroTitle>
      <HeroDescription>{t("mcp.description")}</HeroDescription>
    </HeroSection>
  );
};

const ServerUrlCard = (props: ServerUrlCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <ServerCard aria-live="polite">
      <CardTitle level={2}>{t("mcp.server.title")}</CardTitle>
      <CardDescription>{t("mcp.server.description")}</CardDescription>
      <ServerUrlContent {...props} />
    </ServerCard>
  );
};

const ServerUrlContent = (props: ServerUrlCardProps) => {
  const { isError, isLoading, isRefetching, onRetry, serverUrl } = props;
  if (isLoading)
    return <Skeleton active paragraph={{ rows: 1 }} title={false} />;
  if (isError || !serverUrl)
    return <ServerUrlError isLoading={isRefetching} onRetry={onRetry} />;
  return <ServerUrlField serverUrl={serverUrl} />;
};

const ServerUrlError = ({
  isLoading,
  onRetry,
}: {
  isLoading: boolean;
  onRetry: () => void;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <ErrorState>
      <Text>{t("mcp.server.error")}</Text>
      <Button
        icon={
          <LookIcon
            classic={<ReloadOutlined />}
            newLook={<RefreshCw size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />}
          />
        }
        loading={isLoading}
        onClick={onRetry}
      >
        {t("mcp.server.retry")}
      </Button>
    </ErrorState>
  );
};

const ServerUrlField = ({ serverUrl }: { serverUrl: string }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <UrlField>
      <ServerUrl>{serverUrl}</ServerUrl>
      <CopyButton
        data-testid={MCP_COPY_BUTTON_ID}
        icon={
          <LookIcon
            classic={<CopyOutlined />}
            newLook={<Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />}
          />
        }
        name={t("mcp.server.copy_name")}
        text={serverUrl}
        type="primary"
      >
        {t("mcp.server.copy")}
      </CopyButton>
    </UrlField>
  );
};

const getInstallationTabItems = (
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>) => string,
  serverUrl: string
) =>
  INSTALLATION_CLIENTS.map(({ key, snippetType }) => ({
    key,
    label: t(`mcp.installation.clients.${key}.label`),
    children: (
      <InstallationPanel
        client={key}
        serverUrl={serverUrl}
        snippetType={snippetType}
      />
    ),
  }));

const Installation = ({ serverUrl }: { serverUrl: string }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const items = getInstallationTabItems(t, serverUrl);

  return (
    <InstallationSection>
      <SectionTitle level={2}>{t("mcp.installation.title")}</SectionTitle>
      <SectionDescription>
        {t("mcp.installation.description")}
      </SectionDescription>
      <InstallationCard>
        <InstallationTabs items={items} />
      </InstallationCard>
    </InstallationSection>
  );
};

const InstallationPanel = ({
  client,
  serverUrl,
  snippetType,
}: {
  client: InstallationClient;
  serverUrl: string;
  snippetType: SnippetType;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const snippet = getInstallationSnippet(client, serverUrl);

  return (
    <InstructionList>
      {INSTALLATION_STEPS.map((step, index) => (
        <InstructionStep key={step}>
          <InstructionText>
            {t(`mcp.installation.clients.${client}.${step}`)}
          </InstructionText>
          {index === 1 && (
            <Snippet>
              <SnippetHeader>
                <SnippetLabel>
                  {t(`mcp.installation.snippet.${snippetType}`)}
                </SnippetLabel>
                <SnippetCopyButton
                  icon={
                    <LookIcon
                      classic={<CopyOutlined />}
                      newLook={
                        <Copy size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
                      }
                    />
                  }
                  name={t("mcp.installation.snippet.copy_name")}
                  text={snippet}
                  type="text"
                >
                  {t("mcp.installation.snippet.copy")}
                </SnippetCopyButton>
              </SnippetHeader>
              <SnippetCode>
                <code>{snippet}</code>
              </SnippetCode>
            </Snippet>
          )}
        </InstructionStep>
      ))}
    </InstructionList>
  );
};

const Capabilities = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <CapabilitiesSection>
      <SectionTitle level={2}>{t("mcp.capabilities.title")}</SectionTitle>
      <SectionDescription>
        {t("mcp.capabilities.description")}
      </SectionDescription>
      <CapabilitiesGrid>
        <CapabilityCard
          icon={
            <LookIcon
              classic={<FundOutlined />}
              newLook={
                <ChartLine size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
              }
            />
          }
          translationKey="portfolio"
        />
        <CapabilityCard
          icon={
            <LookIcon
              classic={<SwapOutlined />}
              newLook={
                <ArrowLeftRight size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
              }
            />
          }
          translationKey="trades"
        />
        <CapabilityCard
          icon={
            <LookIcon
              classic={<RobotOutlined />}
              newLook={<Bot size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />}
            />
          }
          translationKey="bots"
        />
      </CapabilitiesGrid>
    </CapabilitiesSection>
  );
};

const CapabilityCard = ({ icon, translationKey }: CapabilityCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <Capability>
      <CapabilityIcon aria-hidden="true">{icon}</CapabilityIcon>
      <CapabilityTitle level={3}>
        {t(`mcp.capabilities.${translationKey}.title`)}
      </CapabilityTitle>
      <CapabilityDescription>
        {t(`mcp.capabilities.${translationKey}.description`)}
      </CapabilityDescription>
      <Example>{t(`mcp.capabilities.${translationKey}.example`)}</Example>
    </Capability>
  );
};

const SecurityNote = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  return (
    <SecurityCard>
      <LookIcon
        classic={<SafetyCertificateOutlined aria-hidden="true" />}
        newLook={
          <ShieldCheck
            className="mcp-note-icon"
            size={18}
            strokeWidth={NEW_LOOK_ICON_STROKE}
            aria-hidden="true"
          />
        }
      />
      <div>
        <SecurityTitle>
          {!isNewLook && <CheckCircleOutlined aria-hidden="true" />}
          {t("mcp.security.title")}
        </SecurityTitle>
        <SecurityDescription>
          {t("mcp.security.description")}
        </SecurityDescription>
      </div>
    </SecurityCard>
  );
};

const AiDisclaimer = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <DisclaimerCard>
      <LookIcon
        classic={<WarningOutlined aria-hidden="true" />}
        newLook={
          <TriangleAlert
            className="mcp-note-icon"
            size={18}
            strokeWidth={NEW_LOOK_ICON_STROKE}
            aria-hidden="true"
          />
        }
      />
      <div>
        <DisclaimerTitle>{t("mcp.disclaimer.title")}</DisclaimerTitle>
        <DisclaimerDescription>
          {t("mcp.disclaimer.description")}
        </DisclaimerDescription>
      </div>
    </DisclaimerCard>
  );
};

/** New look: what MCP is, as the side column's first panel. */
const NewLookAbout = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <Panel title={t("mcp.title")}>
      <NewLookBody>{t("mcp.description")}</NewLookBody>
    </Panel>
  );
};

const NEW_LOOK_CAPABILITIES: {
  icon: typeof ChartLine;
  translationKey: CapabilityCardProps["translationKey"];
}[] = [
  { icon: ChartLine, translationKey: "portfolio" },
  { icon: ArrowLeftRight, translationKey: "trades" },
  { icon: Bot, translationKey: "bots" },
];

/** New look: the capabilities as hairline rows with a quiet example each. */
const NewLookCapabilities = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <Panel title={t("mcp.capabilities.title")} flush>
      <NewLookFlushIntro>{t("mcp.capabilities.description")}</NewLookFlushIntro>
      {NEW_LOOK_CAPABILITIES.map(({ icon: Icon, translationKey }) => (
        <NewLookRow key={translationKey}>
          <NewLookRowIcon aria-hidden="true">
            <Icon size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          </NewLookRowIcon>
          <div>
            <NewLookRowTitle>
              {t(`mcp.capabilities.${translationKey}.title`)}
            </NewLookRowTitle>
            <NewLookRowText>
              {t(`mcp.capabilities.${translationKey}.description`)}
            </NewLookRowText>
            <NewLookExample>
              {t(`mcp.capabilities.${translationKey}.example`)}
            </NewLookExample>
          </div>
        </NewLookRow>
      ))}
    </Panel>
  );
};

/** New look: security and the AI disclaimer as two quiet rows. */
const NewLookNotes = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <NewLookNotesPanel>
      <NewLookRow as="aside">
        <NewLookRowIcon aria-hidden="true">
          <ShieldCheck size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
        </NewLookRowIcon>
        <div>
          <NewLookRowTitle>{t("mcp.security.title")}</NewLookRowTitle>
          <NewLookRowText>{t("mcp.security.description")}</NewLookRowText>
        </div>
      </NewLookRow>
      <NewLookRow as="aside">
        <NewLookRowIcon aria-hidden="true">
          <TriangleAlert size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
        </NewLookRowIcon>
        <div>
          <NewLookRowTitle>{t("mcp.disclaimer.title")}</NewLookRowTitle>
          <NewLookRowText>{t("mcp.disclaimer.description")}</NewLookRowText>
        </div>
      </NewLookRow>
    </NewLookNotesPanel>
  );
};

const INSTALLATION_CLIENTS: InstallationClientConfig[] = [
  { key: "claude", snippetType: "command" },
  { key: "cursor", snippetType: "configuration" },
  { key: "vscode", snippetType: "configuration" },
  { key: "codex", snippetType: "command" },
  { key: "gemini", snippetType: "configuration" },
];
const INSTALLATION_STEPS: InstallationStep[] = [
  "step_1",
  "step_2",
  "step_3",
  "step_4",
];

const getInstallationSnippet = (
  client: InstallationClient,
  serverUrl: string
) => {
  switch (client) {
    case "claude":
      return `claude mcp add --transport http cornix ${serverUrl}`;
    case "codex":
      return `codex mcp add cornix --url ${serverUrl}\ncodex mcp login cornix`;
    case "vscode":
      return JSON.stringify(
        {
          servers: {
            cornix: {
              type: "http",
              url: serverUrl,
            },
          },
        },
        null,
        2
      );
    case "gemini":
      return JSON.stringify(
        {
          mcpServers: {
            cornix: {
              httpUrl: serverUrl,
            },
          },
        },
        null,
        2
      );
    case "cursor":
      return JSON.stringify(
        {
          mcpServers: {
            cornix: {
              url: serverUrl,
            },
          },
        },
        null,
        2
      );
  }
};

const Page = styled.main`
  width: min(1120px, 100%);
  margin: 0 auto;
  padding: 56px 40px 72px;
  color: ${(p) => p.theme.colors.textColor};
  @media (max-width: 720px) {
    padding: 32px 16px 48px;
  }
`;
const HeroSection = styled.section`
  max-width: 780px;
  margin: 0 auto 40px;
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      max-width: none;
      margin: 0;
      padding: 20px;
      text-align: left;
      border: ${p.theme.borders.panel};
      border-radius: ${p.theme.radii.card}px;
      background: ${p.theme.colors.cardBgColor};
      box-shadow: ${p.theme.shadows.panel};
      @media (max-width: 720px) {
        padding: 16px;
      }
    `}
`;
const IconBadge = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 18px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
  font-size: 25px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      width: 40px;
      height: 40px;
      margin: 0 0 12px;
      border-radius: 12px;
      font-size: 19px;
    `}
`;
const Eyebrow = styled(Text)`
  margin: 0 0 10px;
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      margin: 0 0 4px;
      font-size: 11.5px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      letter-spacing: 0.08em;
    `}
`;
const HeroTitle = styled(Title)`
  margin: 0;
  font-size: 38px;
  line-height: 1.2;
  @media (max-width: 720px) {
    font-size: 28px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        color: ${p.theme.colors.titleColor};
        font-size: 16px;
        line-height: 1.5;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      }
    `}
`;
const HeroDescription = styled(Text)`
  max-width: 720px;
  margin: 18px auto 0;
  color: ${(p) => p.theme.colors.mediumText};
  font-size: 17px;
  line-height: 1.7;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      max-width: none;
      margin: 4px 0 0;
      color: ${p.theme.colors.secondaryText};
      font-size: 13.5px;
      line-height: 1.6;
    `}
`;
const ServerCard = styled.section`
  padding: 30px;
  border: ${(p) => p.theme.borders.light};
  border-radius: ${(p) => (p.theme.isNewLook ? 12 : 16)}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) => p.theme.shadows.dashboardCard};
  @media (max-width: 720px) {
    padding: 22px 18px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 20px;
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      @media (max-width: 720px) {
        padding: 16px;
      }
    `}
`;
const CardTitle = styled(Title)`
  margin: 0 0 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin: 0 0 4px;
        color: ${p.theme.colors.titleColor};
        font-size: 16px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
      }
    `}
`;
const CardDescription = styled(Text)`
  margin: 0 0 20px;
  color: ${(p) => p.theme.colors.mediumText};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      margin: 0 0 16px;
      color: ${p.theme.colors.secondaryText};
      font-size: 13.5px;
    `}
`;
const UrlField = styled.div`
  min-width: 0;
  display: flex;
  border: ${(p) => p.theme.borders.medium};
  border-radius: ${(p) => (p.theme.isNewLook ? 8 : 10)}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  overflow: hidden;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: center;
      gap: 8px;
      padding: 4px;
      border: ${p.theme.borders.card};
    `}
`;
const ServerUrl = styled.code`
  min-width: 0;
  padding: 14px 16px;
  flex: 1;
  overflow: hidden;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 0 8px 0 10px;
      font-size: 13.5px;
      line-height: 32px;
    `}
`;
const CopyButton = styled(CopyToClipboardButton)`
  && {
    height: auto;
    border-radius: 0;
    flex: none;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Sits inside the field (4px inset) instead of filling its edge. */
      && {
        height: 32px;
        border-radius: ${NEW_LOOK_RADIUS.chip}px;
      }
    `}
`;
const ErrorState = styled.div`
  min-height: 56px;
  padding: 12px 14px;
  border-radius: ${(p) => (p.theme.isNewLook ? 8 : 10)}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${(p) => p.theme.colors.lightBackground};
  ${Text} {
    margin: 0;
  }
`;
const InstallationSection = styled.section`
  margin-top: 52px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
    `}
`;
const InstallationCard = styled.div`
  border: ${(p) => p.theme.borders.light};
  border-radius: ${(p) => (p.theme.isNewLook ? 12 : 16)}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) => p.theme.shadows.dashboardCard};
  overflow: hidden;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
    `}
`;
const InstallationTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin: 0;
    padding: 0 28px;
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  .ant-tabs-tab {
    padding: 18px 0;
    font-weight: 600;
  }
  .ant-tabs-content-holder {
    padding: 28px;
  }
  @media (max-width: 720px) {
    .ant-tabs-nav {
      padding: 0 18px;
    }
    .ant-tabs-content-holder {
      padding: 22px 18px;
    }
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      .ant-tabs-nav {
        padding: 0 20px;
        background: transparent;
      }
      .ant-tabs-nav::before {
        border-bottom-color: ${getNewLookTokens(p.theme).hairline};
      }
      .ant-tabs-tab {
        padding: 12px 0;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
      .ant-tabs-tab + .ant-tabs-tab {
        margin-left: 24px;
      }
      /* Content starts on the tab labels' edge. */
      .ant-tabs-content-holder {
        padding: 20px;
      }
      @media (max-width: 720px) {
        .ant-tabs-nav {
          padding: 0 16px;
        }
        .ant-tabs-content-holder {
          padding: 16px;
        }
      }
    `}
`;
const InstructionList = styled.ol`
  margin: 0;
  padding-left: 24px;
`;
const InstructionStep = styled.li`
  padding-left: 8px;
  color: ${(p) => p.theme.colors.mediumText};
  line-height: 1.6;
  & + & {
    margin-top: 16px;
  }
  &::marker {
    color: ${(p) => p.theme.colors.primaryBlue};
    font-weight: 600;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      &::marker {
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
        font-variant-numeric: tabular-nums;
      }
    `}
`;
const InstructionText = styled(Text)`
  margin: 0;
  color: inherit;
  line-height: inherit;
`;
const Snippet = styled.div`
  min-width: 0;
  margin-top: 12px;
  border: ${(p) => p.theme.borders.medium};
  border-radius: ${(p) => (p.theme.isNewLook ? 8 : 10)}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  overflow: hidden;
`;
const SnippetHeader = styled.div`
  min-height: 42px;
  padding: 6px 8px 6px 14px;
  border-bottom: ${(p) => p.theme.borders.lightest};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The label starts on the code's left edge. */
      min-height: 40px;
      padding: 4px 6px 4px 16px;
    `}
`;
const SnippetLabel = styled.span`
  color: ${(p) => p.theme.colors.mediumText};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 11.5px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      letter-spacing: 0.08em;
    `}
`;
const SnippetCopyButton = styled(CopyToClipboardButton)`
  && {
    height: 30px;
    padding-inline: 10px;
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const SnippetCode = styled.pre`
  margin: 0;
  padding: 16px;
  overflow-x: auto;
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13px;
  line-height: 1.55;
  white-space: pre;
`;
const CapabilitiesSection = styled.section`
  margin-top: 52px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
      padding: 24px;
      border: ${p.theme.borders.panel};
      border-radius: ${p.theme.radii.card}px;
      background: ${p.theme.colors.cardBgColor};
      box-shadow: ${p.theme.shadows.panel};
      @media (max-width: 720px) {
        padding: 16px;
      }
    `}
`;
const SectionDescription = styled(Text)`
  max-width: 660px;
  margin: -10px auto 24px;
  color: ${(p) => p.theme.colors.mediumText};
  line-height: 1.6;
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      max-width: none;
      margin: 0 0 16px;
      color: ${p.theme.colors.secondaryText};
      font-size: 13.5px;
      text-align: left;
    `}
`;
const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      grid-template-columns: minmax(0, 1fr);
      gap: 0;
    `}
`;
const Capability = styled.article`
  padding: 26px;
  border: ${(p) => p.theme.borders.light};
  border-radius: 16px;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: grid;
      grid-template-columns: 32px minmax(0, 1fr);
      column-gap: 12px;
      padding: 16px 0 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      & + & {
        margin-top: 16px;
        border-top: ${p.theme.borders.lightest};
      }
      > * {
        grid-column: 2;
      }
    `}
`;
const CapabilityIcon = styled.div`
  width: 42px;
  height: 42px;
  margin-bottom: 20px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
  font-size: 20px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      grid-column: 1 !important;
      grid-row: 1 / span 3;
      width: 32px;
      height: 32px;
      margin: 0;
      border-radius: 8px;
      font-size: 15px;
    `}
`;
const CapabilityTitle = styled(Title)`
  margin: 0 0 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin: 0 0 2px;
        color: ${p.theme.colors.titleColor};
        font-size: 14px;
        line-height: 1.5;
        font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      }
    `}
`;
const CapabilityDescription = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.mediumText};
  line-height: 1.6;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      color: ${p.theme.colors.secondaryText};
      font-size: 13px;
      line-height: 1.5;
    `}
`;
const Example = styled.div`
  margin-top: 18px;
  padding: 10px 12px;
  border-radius: ${(p) => (p.theme.isNewLook ? 8 : 9)}px;
  color: ${(p) => p.theme.colors.primaryText};
  background: ${(p) => p.theme.colors.cardLightBgColor};
  font-size: 13px;
  font-style: italic;
  line-height: 1.45;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 8px;
      padding: 6px 10px;
      font-size: 12.5px;
      justify-self: start;
    `}
`;
const SectionTitle = styled(Title)`
  margin: 0 0 20px;
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin: 0 0 4px;
        color: ${p.theme.colors.titleColor};
        font-size: 16px;
        font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
        text-align: left;
      }
    `}
`;
const SecurityCard = styled.aside`
  margin-top: 20px;
  padding: 18px 20px;
  border: 1px solid ${(p) => p.theme.colors.midBlue};
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
  > .anticon {
    margin-top: 2px;
    font-size: 22px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
      padding: 16px;
      gap: 12px;
      border-color: transparent;
      > .mcp-note-icon {
        flex: none;
        margin-top: 2px;
      }
    `}
`;
const SecurityTitle = styled.div`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: ${(p) => p.theme.colors.primaryText};
  font-weight: 600;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      line-height: 22px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    `}
`;
const SecurityDescription = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.mediumText};
  line-height: 1.55;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      font-size: 13px;
    `}
`;
const DisclaimerCard = styled.aside`
  margin-top: 16px;
  padding: 16px 20px;
  border: ${(p) => p.theme.borders.lightest};
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  color: ${(p) => p.theme.colors.mediumText};
  background: ${(p) => p.theme.colors.cardLightBgColor};
  > .anticon {
    margin-top: 2px;
    font-size: 18px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 0;
      padding: 16px;
      gap: 12px;
      border-color: transparent;
      > .mcp-note-icon {
        flex: none;
        margin-top: 2px;
      }
    `}
`;
const DisclaimerTitle = styled.div`
  margin-bottom: 4px;
  color: ${(p) => p.theme.colors.primaryText};
  font-weight: 600;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      line-height: 22px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    `}
`;
const DisclaimerDescription = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.mediumText};
  font-size: 13px;
  line-height: 1.55;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      font-size: 12.5px;
    `}
`;

const NEW_LOOK_ASIDE_WIDTH = 340;
const NEW_LOOK_STACKED = "1180px";

const NewLookPage = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${NEW_LOOK_ASIDE_WIDTH}px;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    "main hero"
    "main aside";
  gap: 16px ${NEW_LOOK_SECTION_GAP}px;
  align-items: start;
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} 40px;
  color: ${(p) => p.theme.colors.textColor};
  @media (max-width: ${NEW_LOOK_STACKED}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    grid-template-areas:
      "hero"
      "main"
      "aside";
  }
  @media (max-width: ${phoneMaxWidth}) {
    padding: 12px 16px 24px;
  }
`;
const NewLookHero = styled.div`
  grid-area: hero;
  min-width: 0;
`;
const NewLookMain = styled.div`
  grid-area: main;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
`;
const NewLookAside = styled.div`
  grid-area: aside;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const NewLookIntro = styled.p`
  margin: 0 0 12px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookFlushIntro = styled(NewLookIntro)`
  margin: 0;
  padding: 14px 20px 0;
`;
const NewLookBody = styled.p`
  margin: 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookRow = styled.div`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  column-gap: 12px;
  padding: 14px 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookRowIcon = styled.span`
  display: inline-flex;
  margin-top: 2px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookRowTitle = styled.h3`
  margin: 0;
  ${newLookType.body};
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookRowText = styled.p`
  margin: 2px 0 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookExample = styled.div`
  display: inline-block;
  margin-top: 8px;
  padding: 4px 8px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.meta};
  font-style: italic;
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const NewLookNotesPanel = styled.div`
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
`;
